import { useCallback, useEffect, useMemo, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useNavigate, useParams } from "react-router-dom"
import TextareaAutosize from 'react-textarea-autosize'
import api from "../../api"
import { ReactComponent as CloseIcon } from "../../assets/images/icons/ic-close.svg"
import { ButtonStatus, FileType, MacroBackgroundColors, MacroBackgrounds } from "../../common/constants"
import { Each } from "../../common/Each"
import Select from "../../components/Select"
import { DialogStatus } from "../../enums"
import typo from "../../typography.module.css"
import { getDefaultSection } from "../../utils"
import Back from "../Back"
import Card from "../cards/Card"
import AlertDialog from "../dialogs/AlertDialog"
import DropdownMultiSelection from "../DropdownMultiSelection"
import HeaderFooterLayout from "../layouts/HeaderFooterLayout"
import PageBuilder from "../builder/PageBuilder"
import TextInput from "../TextInput"
import Uploader from "../Uploader"
import MacroCardPreview from "./MacroCardPreview"
import styles from "./MacroEditor.module.css"
import MacroMainPreview from "./MacroMainPreview"
import { ReactComponent as NewWindowIcon } from "../../assets/images/icons/ic-new-window.svg"

const MacroEditor = () => {

  const { macroId } = useParams()
  const navigate = useNavigate()
  const [macro, setMacro] = useState(null)
  const [sections, setSections] = useState(null)
  const [careers, setCareers] = useState([])
  const [selectedCareers, setSelectedCareers] = useState([])
  const [name, setName] = useState("")
  const [subtitle, setSubtitle] = useState("")
  const [background, setBackground] = useState(null)
  const [description, setDescription] = useState("")
  const [thumbnail, setThumbnail] = useState(null)
  const [videoUrl, setVideoUrl] = useState("")
  const [canBePublished, setCanBePublished] = useState(false)
  const [publishingStatus, setPublishingStatus] = useState(null)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [closeTimeout, setCloseTimeout] = useState(null)
  const [uploadVideoReset, setUploadVideoReset] = useState(false)
  const [uploadThumbnailReset, setUploadThumbnailReset] = useState(false)
  const statusOptions = useMemo(() => {
    return [
      { id: 0, name: 'Bozza', value: 'draft' },
      { id: 1, name: 'Pubblico', value: 'public' },
      { id: 2, name: 'Nascosto', value: 'hidden' }
    ]
  }, [])
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])

  const [selectedColor, setSelectedColor] = useState(MacroBackgroundColors.Purple)
  const [selectedBackground, setSelectedBackground] = useState(MacroBackgrounds.Background1)

  useEffect(() => {
    const getMacro = async () => {
      try {
        const macro = await api.get(`/admin/macros/${macroId}`)
        setMacro(macro)
        const {
          name,
          subtitle,
          description,
          video_url,
          thumbnail,
          content,
          background,
          status,
          careers
        } = macro
        setName(name)
        setSubtitle(subtitle)
        setDescription(description)
        setVideoUrl(video_url)
        setThumbnail(thumbnail)
        setSections(content)
        setBackground(background)
        setSelectedCareers(careers ?? [])
        setSelectedStatus(statusOptions.find(s => s.value === status))
      } catch (e) {
        console.error(e)
      }
    }

    const getCareers = async () => {
      try {
        const res = await api.get("/admin/careers")
        setCareers(res)
      } catch (e) {
        console.error(e)
      }
    }

    if (macroId !== "create") {
      getMacro()
    } else {
      setSections([getDefaultSection()])
    }
    getCareers()

    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
    }
  }, [])

  useEffect(() => {
    setCanBePublished(!!name && !!description && !!videoUrl && !!subtitle && !!background && selectedCareers.length > 0)
    if (background) {
      for (let color of Object.values(MacroBackgroundColors)) {
        if (background.includes(color.primary) && background.includes(color.secondary)) {
          setSelectedColor(color)
          let defaultBg = background.replace(color.primary, '%primary%').replace(color.secondary, '%secondary%')
          for (let background of Object.values(MacroBackgrounds)) {
            if (background === defaultBg) {
              setSelectedBackground(defaultBg)
            }
          }
        }
      }
    }
  }, [name, description, videoUrl, subtitle, background, selectedCareers])


  const publish = useCallback(async () => {
    setPublishingStatus(ButtonStatus.Loading)
    setAlertStatus(DialogStatus.Loading)

    try {
      const slug = name.replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
        .replace(/\s+/g, '-') // Sostituisci spazi con trattini
        .toLowerCase()
        .trim()
      const body = {
        name,
        subtitle,
        description,
        status: selectedStatus.value,
        video_url: videoUrl,
        thumbnail,
        content: JSON.stringify(sections),
        slug,
        background: applyColorsToBackground(selectedBackground, selectedColor),
        careers_ids: JSON.stringify(selectedCareers.map(c => c.id))
      }
      const res = macroId !== "create" ?
        await api.put(`/admin/macros/${macroId}`, body) :
        await api.post("/admin/macros", body)

      setPublishingStatus(ButtonStatus.Success)
      setAlertStatus(DialogStatus.Success)
      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 3000))
      setUploadThumbnailReset(!uploadThumbnailReset)
      setUploadVideoReset(!uploadVideoReset)
      // navigate(`/macros/${res.id}`)
      // window.location.reload()
    } catch (e) {
      setPublishingStatus(ButtonStatus.Error)
      setAlertStatus(DialogStatus.Error)

      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 3000))
      console.error(e)
    }
  }, [name, subtitle, sections, description, videoUrl, thumbnail, selectedStatus, macroId, selectedBackground, selectedColor, selectedCareers, navigate, uploadThumbnailReset, uploadVideoReset])

  const applyColorsToBackground = (background, color) => {
    return background.replace('%primary%', color.primary).replace('%secondary%', color.secondary)
  }

  return (
    <HeaderFooterLayout hideFooter>
      <HelmetProvider>
        <Helmet>
          <title>Area di interesse</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Back onClick={() => navigate("/macros")} />
            <div className={styles.title}>
              <div className={typo.title}>Area di interesse</div>
            </div>


            <div className={styles.header}>
              <div className={styles.headerLeft} style={{ width: "70%" }}>
                <Select selected={selectedStatus} options={statusOptions} onSelect={(value) => setSelectedStatus(value)} />
                <TextInput
                  value={name}
                  fullWidth
                  type="text"
                  placeholder={"Titolo area di interesse"}
                  onKeyUp={(s) => setName(s)}
                />
                <TextInput
                  value={subtitle}
                  fullWidth
                  type="text"
                  placeholder={"Sottotitolo area di interesse"}
                  onKeyUp={(s) => setSubtitle(s)}
                />
                <TextareaAutosize
                  value={description}
                  minRows={3} maxRows={10}
                  type="text"
                  className={styles.textArea}
                  placeholder="Descrizione"
                  onChange={(e) => {
                    const { value } = e.target
                    setDescription(value)
                  }} />
                <DropdownMultiSelection
                  options={careers}
                  selectedOptions={selectedCareers}
                  placeholder="Associa le carriere all'area di interesse"
                  itemsRender={(career, i) => (
                    <div className={styles.career} onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const idx = selectedCareers.findIndex(c => c.id === career.id)
                      if (idx >= 0) {
                        setSelectedCareers(c => {
                          c.splice(idx, 1)
                          return [...c]
                        })
                        setCareers(c => [...c, career])
                      } else {
                        setSelectedCareers(c => [...c, career])
                        setCareers(c => {
                          const cIdx = c.findIndex(c => c.id === career.id)
                          if (cIdx >= 0) {
                            c.splice(cIdx, 1)
                          }
                          return [...c]
                        })
                      }
                    }}>
                      {career.name}
                      {
                        selectedCareers.findIndex(c => c.id === career.id) >= 0 &&
                        <CloseIcon className={styles.removeCareer} />
                      }
                    </div>
                  )}
                />
                <div className={styles.medias}>
                  <div className={styles.mediaContainer}>
                    <div className={styles.label}>Video copertina</div>
                    <div className={styles.mediaTop}>
                      <Uploader
                        preview={videoUrl}
                        reset={uploadVideoReset}
                        multiple={false}
                        type={FileType.Video}
                        onComplete={uploads => setVideoUrl(uploads[0].url)}
                        onDelete={() => setVideoUrl(null)}
                      />
                      {
                        !videoUrl &&
                        <div className={styles.mediaHelperText}>Oppure inserisci l'URL del video</div>
                      }
                    </div>

                    <Card hover style={{ padding: ".5rem 1rem", marginTop: ".5rem", width: "100%" }} title={`URL video`}>
                      <TextInput
                        type={"text"}
                        value={videoUrl}
                        onKeyUp={v => setVideoUrl(v)}
                        style={{
                          padding: 0, border: "none", backgroundColor: "transparent",
                          fontSize: "1rem", fontWeight: 700, width: "100%",
                          borderRadius: 0
                        }} />
                    </Card>
                  </div>
                  <div className={styles.mediaContainer}>
                    <div className={styles.label}>Immagine copertina</div>
                    <div className={styles.mediaTop}>
                      <Uploader
                        crop={true}
                        iconButtons={true}
                        preview={thumbnail}
                        reset={uploadThumbnailReset}
                        multiple={false}
                        type={FileType.Image}
                        onComplete={uploads => setThumbnail(uploads[0].url)}
                        onDelete={() => setThumbnail(null)}
                      />
                      {
                        !thumbnail &&
                        <div className={styles.mediaHelperText}>Oppure inserisci l'URL dell'immagine</div>
                      }
                    </div>
                    <Card hover style={{ padding: ".5rem 1rem", marginTop: ".5rem", width: "100%" }} title={`URL immagine`}>
                      <TextInput
                        type={"text"}
                        value={thumbnail}
                        onKeyUp={v => setThumbnail(v)}
                        style={{
                          padding: 0, border: "none", backgroundColor: "transparent",
                          fontSize: "1rem", fontWeight: 700, width: "100%",
                          borderRadius: 0
                        }} />
                    </Card>
                  </div>
                </div>
              </div>
              <div className={styles.headerRight} style={{ width: "30%" }}>
                <div className={styles.cardPreview}>
                  <div className={typo.caption}>ANTEPRIMA</div>
                  <MacroCardPreview
                    macro={{
                      name, subtitle, description,
                      background: applyColorsToBackground(selectedBackground, selectedColor)
                    }}
                  />
                </div>
                <div className={styles.backgrounds}>
                  <Each of={Object.values(MacroBackgrounds)} render={(background, i) => {
                    return (
                      <div className={`${styles.background} ${background === selectedBackground ? styles.selected : ''}`}
                        dangerouslySetInnerHTML={{ __html: applyColorsToBackground(background, selectedColor) }}
                        onClick={() => { setSelectedBackground(background) }}
                      />
                    )
                  }} />
                </div>
                <div className={styles.colors}>
                  <Each of={Object.values(MacroBackgroundColors)} render={(color, i) => {
                    return (
                      <div className={styles.colorWrapper}>
                        <div className={`${styles.color} ${color.primary === selectedColor.primary && color.secondary === selectedColor.secondary ? styles.selected : ''}`}
                          onClick={() => {
                            setSelectedColor(color)
                          }}>
                          <div className={styles.primary} style={{ backgroundColor: color.primary }}>
                          </div>
                          <div className={styles.secondary} style={{ backgroundColor: color.secondary }}>
                          </div>
                        </div>
                      </div>
                    )
                  }} />
                </div>
              </div>
            </div>

            <div className={styles.header}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '1rem' }}>
                <div className={typo.subtitle} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '1rem' }}>
                  Anteprima
                  <NewWindowIcon style={{ cursor: 'pointer' }} onClick={() => {
                    window.open(`/macros/${macroId}/preview`)
                  }}></NewWindowIcon> </div>
                <Card>
                  <div className={styles.macroMainPreview}>
                    {macro &&
                      <MacroMainPreview name={name} description={description} careers={selectedCareers} video_url={videoUrl} thumbnail={thumbnail} />
                    }
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.section}>
          {sections &&
            <PageBuilder
              content={sections}
              onSave={() => {
                setOpenAlert(true)
              }}
              onContentChange={(page) => {
                setSections(page)
              }} />
          }
        </div>

        <AlertDialog
          open={openAlert}
          title={"Pubblica area di interesse"}
          text={"Prima di pubblicare verifica di aver impostato correttamente contenuto e stile tramite l'anteprima.\n Sei sicuro di voler pubblicare l'area di interesse?"}
          status={alertStatus}
          onClose={() => {
            if (alertStatus === DialogStatus.Success) {
            }

            setOpenAlert(false)
            setAlertStatus(DialogStatus.Default)
            if (closeTimeout) {
              clearTimeout(closeTimeout)
            }
          }}
          actions={[
            {
              label: alertStatus === DialogStatus.Success ? "OK" : "PUBBLICA",
              onClick: alertStatus === DialogStatus.Success ? () => { setOpenAlert(false) } : publish,
            }
          ]}
        />
      </div>

    </HeaderFooterLayout>
  )
}


export default MacroEditor
